import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Card } from '@cortex/web-design-system/components/ui/Card';
import type { PurchaseChartPoint } from '../../lib/purchase-aggregation.js';
import {
  CHART_COLORS,
  TOOLTIP_CONTENT_STYLE,
  AXIS_TICK_STYLE,
  GRID_PROPS,
  LEGEND_CLICKABLE_PROPS,
} from '../../lib/chart-theme.js';
import { useSeriesToggle } from '../../hooks/useSeriesToggle.js';

/** @graph-connects none */
type ChartView = 'rate' | 'unitPrice' | 'count' | 'revenue';

/** labelKey は描画側で t() する翻訳キー。 @graph-connects none */
const VIEWS: { key: ChartView; labelKey: string }[] = [
  { key: 'rate', labelKey: 'purchase.rate' },
  { key: 'unitPrice', labelKey: 'purchase.unitPrice' },
  { key: 'count', labelKey: 'purchase.count' },
  { key: 'revenue', labelKey: 'purchase.revenue' },
];

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Finance
 * @graph-business 購入分析の推移チャート群。購入率・購入単価・購入着数・購入売上の4指標を切替表示。凡例クリックで系列ON/OFF。
 * @graph-connects none
 */
export function PurchaseTrendCharts({ data }: { data: PurchaseChartPoint[] }) {
  const { t } = useTranslation();
  const [view, setView] = useState<ChartView>('rate');
  const { hiddenSeries, handleLegendClick, resetHidden } = useSeriesToggle();

  /** @graph-connects none */
  const fmtJpy = ((value: number, name: string) => [
    `¥${value.toLocaleString('ja-JP')}`,
    name,
  ]) as never;

  /** @graph-connects none */
  const fmtNum = ((value: number, name: string) => [value.toLocaleString('ja-JP'), name]) as never;

  return (
    <Card className="p-4">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-[var(--color-text-primary)]">
          {t('purchase.trendChartTitle')}
        </h3>
        <div className="ml-auto flex rounded-lg border border-[var(--color-border)]">
          {VIEWS.map((v, i) => (
            <button
              key={v.key}
              type="button"
              onClick={() => {
                setView(v.key);
                resetHidden();
              }}
              className={`px-3 py-1 text-xs transition-colors ${
                view === v.key
                  ? 'bg-[var(--color-accent-primary)] text-white'
                  : 'text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-subtle)]'
              } ${i === 0 ? 'rounded-l-lg' : 'border-l border-[var(--color-border)]'} ${i === VIEWS.length - 1 ? 'rounded-r-lg' : ''}`}
            >
              {t(v.labelKey)}
            </button>
          ))}
        </div>
      </div>

      <div style={{ width: '100%', height: 340 }}>
        <ResponsiveContainer width="100%" height="100%">
          {view === 'rate' ? (
            <ComposedChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
              <YAxis
                yAxisId="left"
                unit="%"
                tick={AXIS_TICK_STYLE}
                tickLine={false}
                axisLine={false}
                domain={[0, 'auto']}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                tick={AXIS_TICK_STYLE}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
              />
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} formatter={fmtNum} />
              <Legend {...LEGEND_CLICKABLE_PROPS} onClick={handleLegendClick} />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="purchaseRatePct"
                name={t('purchase.rateLegend')}
                stroke={CHART_COLORS[0]}
                strokeWidth={2}
                dot={{ r: 3 }}
                hide={hiddenSeries.has('purchaseRatePct')}
              />
              <Bar
                yAxisId="right"
                dataKey="purchaseCount"
                name={t('purchase.count')}
                fill={CHART_COLORS[2]}
                opacity={0.6}
                hide={hiddenSeries.has('purchaseCount')}
              />
            </ComposedChart>
          ) : view === 'unitPrice' ? (
            <LineChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
              <YAxis
                tick={AXIS_TICK_STYLE}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => `¥${v.toLocaleString('ja-JP')}`}
              />
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} formatter={fmtJpy} />
              <Legend {...LEGEND_CLICKABLE_PROPS} onClick={handleLegendClick} />
              <Line
                type="monotone"
                dataKey="averageUnitPrice"
                name={t('purchase.averageUnitPrice')}
                stroke={CHART_COLORS[1]}
                strokeWidth={2}
                dot={{ r: 3 }}
                hide={hiddenSeries.has('averageUnitPrice')}
              />
            </LineChart>
          ) : view === 'count' ? (
            <BarChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
              <YAxis
                tick={AXIS_TICK_STYLE}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => v.toLocaleString('ja-JP')}
              />
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} formatter={fmtNum} />
              <Legend {...LEGEND_CLICKABLE_PROPS} onClick={handleLegendClick} />
              <Bar
                dataKey="purchaseCount"
                name={t('purchase.count')}
                fill={CHART_COLORS[0]}
                hide={hiddenSeries.has('purchaseCount')}
              />
              <Bar
                dataKey="deliveryItemCount"
                name={t('purchase.deliveryItemCount')}
                fill={CHART_COLORS[2]}
                hide={hiddenSeries.has('deliveryItemCount')}
              />
            </BarChart>
          ) : (
            <BarChart data={data} margin={{ top: 4, right: 16, bottom: 4, left: 8 }}>
              <CartesianGrid {...GRID_PROPS} />
              <XAxis dataKey="label" tick={AXIS_TICK_STYLE} tickLine={false} />
              <YAxis
                tick={AXIS_TICK_STYLE}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v: number) => `¥${v.toLocaleString('ja-JP')}`}
              />
              <Tooltip contentStyle={TOOLTIP_CONTENT_STYLE} formatter={fmtJpy} />
              <Legend {...LEGEND_CLICKABLE_PROPS} onClick={handleLegendClick} />
              <Bar
                dataKey="purchaseRevenue"
                name={t('purchase.revenue')}
                fill={CHART_COLORS[1]}
                hide={hiddenSeries.has('purchaseRevenue')}
              />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      <p className="mt-2 text-xs text-[var(--color-text-muted)]">{t('purchase.rateDescription')}</p>
    </Card>
  );
}
